import { ListCard, useRouter } from '@capsicum/common-web';
import {
	type FanTokenOfferingCardProps,
	FanTokenOfferingsPage,
	useFanTokenOfferingCard
} from '@capsicum/features/fan-token-offering/shared';
import { useCallback, useMemo } from 'react';

import { useFanTokenOfferingBuy } from '../hooks';

import { BuyButton } from './components/BuyButton';
import { FTOClaimButton } from './components/FTOClaimButton';
import * as S from './FanTokenOffering.styled';
import { FanTokenOfferingCardRibbon } from './FanTokenOfferingCardRibbon';

/**
 * Card used to display an FTO related to a partner on HomePage or on marketplace should be used in a carousel component, or with isFullWidthCard prop set to true.
 *
 * @param {Object} fto - Details of the fan token offering campaign
 * @param {boolean} isFullWidthCard - Whether the card should span the full width of the parent container
 * @param {FanTokenOfferingsPage} page - Page where the card is displayed
 * @param {Number} ftoIndex - Index of the card in the carousel
 * @param {Function} onElapsed - Function to handle card status change
 *
 * @returns {JSX.Element} The card component.
 */
export const FanTokenOfferingCard = ({
	fto,
	ftoIndex,
	isFullWidthCard = false,
	onElapsed,
	page
}: FanTokenOfferingCardProps) => {
	const { push } = useRouter();
	const { buy } = useFanTokenOfferingBuy(fto, page);
	const {
		almostAmount,
		amount,
		claimableExpirationDate,
		claimableStartDate,
		handleElapsed,
		hasReachedClaimEndDate,
		isClaimable,
		isFanTokenOfferingHome,
		isOpened,
		logoSrc,
		parsedStatus,
		ribbonColor,
		setClaimableFTOInfo,
		showClaimButton,
		startDate
	} = useFanTokenOfferingCard({
		fto,
		ftoIndex,
		onElapsed,
		page
	});

	const onClaimClick = useCallback(() => {
		setClaimableFTOInfo();
		push(`/fan-token-offering/claim`);
	}, [setClaimableFTOInfo, push]);

	const handleClick = useCallback(() => {
		if (isFanTokenOfferingHome) push(`/partner/${fto.fanToken.partnerId}`);
		else if (isOpened) buy();
		else if (isClaimable) onClaimClick();
	}, [isFanTokenOfferingHome, push, isOpened, buy, fto.fanToken.partnerId, isClaimable, onClaimClick]);

	const onClick = useMemo(() => {
		return isFanTokenOfferingHome || isOpened || isClaimable ? handleClick : undefined;
	}, [isFanTokenOfferingHome, isOpened, isClaimable, handleClick]);

	const disabled = useMemo(() => {
		return page === FanTokenOfferingsPage.Partner && !isOpened && !isClaimable;
	}, [page, isOpened, isClaimable]);

	return (
		<S.ListCardForFTO
			$disabled={disabled}
			$fullWidth={isFullWidthCard}
			testID={`${page}-ftoCardPartnerId:${fto.fanToken.partnerId}-listItem`}
			onClick={onClick}
		>
			<ListCard.Ribbon backgroundColor={ribbonColor}>
				<FanTokenOfferingCardRibbon
					claimableExpirationDate={claimableExpirationDate}
					claimableStartDate={claimableStartDate}
					handleElapsed={handleElapsed}
					parsedStatus={parsedStatus}
					shortCode={fto.fanToken.shortCode}
					startDate={startDate}
				/>
			</ListCard.Ribbon>
			<S.Container>
				<S.StartContainer>
					<ListCard.Image optimized src={logoSrc} onClick={onClick} />
				</S.StartContainer>
				<S.CardBody>
					<ListCard.Row>
						<ListCard.Title>{fto.fanToken.partner.name}</ListCard.Title>
					</ListCard.Row>
					<ListCard.Row>
						<S.Subtitle>
							<S.BaseCurrency>{`1 ${fto.fanToken.shortCode}`}</S.BaseCurrency>
							<S.Amount>{almostAmount}</S.Amount>
							<S.BaseCurrency>{amount}</S.BaseCurrency>
						</S.Subtitle>
					</ListCard.Row>
					<ListCard.Row>
						<S.BuyWrapper>
							<S.BuyContainer>
								{showClaimButton ? (
									<FTOClaimButton
										fto={fto}
										hasReachedClaimEndDate={hasReachedClaimEndDate}
										onClaimClick={onClaimClick}
									/>
								) : (
									<BuyButton
										partnerId={fto.fanToken.partnerId}
										quantityAvailable={fto.capPerUser}
										shortCode={fto.fanToken.shortCode}
										startDate={fto.startDate}
										status={fto.status}
										testID={page}
										onBuyClick={buy}
									/>
								)}
							</S.BuyContainer>
						</S.BuyWrapper>
					</ListCard.Row>
				</S.CardBody>
			</S.Container>
		</S.ListCardForFTO>
	);
};
